import type {
  CharacterDraftMeta,
  HistoryEventRecord,
  OperatingStatus,
  PlotDevice,
  SummaryRecord,
  WorldClock,
  WorldSnapshot,
} from '../../domain/types.js';
import type { CharacterAbsenceRecord } from '../../runtime/characterAbsence.js';
import type { WorldLaunchHandoff } from '../CreateWorld/draftStorage.js';

export const RUNTIME_STATE_SCHEMA_VERSION = '1.1.0';
/** Real-time absence before surfacing "What you missed". */
export const MISSED_ACTIVITY_THRESHOLD_MS = 3 * 60 * 60 * 1000;

/** Runtime-local observation overlay — does not mutate domain event records. */
export interface RuntimeEventObservation {
  eventId: string;
  userObserved: boolean;
  missedWhileAway: boolean;
}

export interface PersistedRuntimeState {
  schemaVersion: typeof RUNTIME_STATE_SCHEMA_VERSION;
  worldId: string;
  clock: WorldClock;
  operatingStatus: OperatingStatus;
  activeDeviceIds: string[];
  usedDevicesThisWeek: string[];
  /** Monday week-start key in world-local calendar (YYYY-MM-DD). */
  deviceWeekKey: string;
  currentInWorldDate: string;
  historyEvents: HistoryEventRecord[];
  summaries: SummaryRecord[];
  eventObservations: RuntimeEventObservation[];
  lastVisitedAt: string;
  missedPromptDismissedAt?: string;
  /** Real epoch ms when inWorldIso was last synced. */
  clockAnchorMs: number;
  /** Holiday / lunch / forever removals — 6mo history, auto-return where applicable. */
  characterAbsences?: CharacterAbsenceRecord[];
  resumePose?: { facingAway?: [string, string] };
  /** Narrative summary of the latest catch-up missed events. */
  missedEventStory?: string | null;
}

export interface AquariumRuntimeView {
  handoff: WorldLaunchHandoff;
  snapshot: WorldSnapshot;
  characters: CharacterDraftMeta[];
  locationLabel: string;
  periodLabel: string;
  /** Notes §12 single-line subtitle. */
  subtitle: string;
  clock: WorldClock;
  operatingStatus: OperatingStatus;
  /** Internal only — never render device ids/names in UI. */
  activeDevices: PlotDevice[];
  historyEvents: HistoryEventRecord[];
  summaries: SummaryRecord[];
  lastVisitedAt: string;
  showMissedPrompt: boolean;
  missedSummary: SummaryRecord | null;
  /** Unobserved events from while the player was away. */
  missedEvents: HistoryEventRecord[];
  missedEventStory: string | null;
  locationSummary: SummaryRecord | null;
  focusedCharacterId: string | null;
  isNarrowViewport: boolean;
  isEmptyRoster: boolean;
  characterAbsences: CharacterAbsenceRecord[];
  catchingUp: boolean;
}

export type RuntimeMenuPanel =
  | 'location-summary'
  | 'location-history'
  | 'character-history-pick'
  | 'character-history'
  | 'add-character'
  | 'remove-character'
  | 'missed'
  | null;
